import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Autocomplete, Chip, Typography, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import TagSelect from './TagSelect';
import { computeDefaultEndDate, calculateDurationWeeks, formatWeeksDisplay } from '../../../../utils/experimentDuration';
import { formatLocalDate } from '../../../../utils/dateUtils';

const BulkReplacement = React.forwardRef(({ 
  sectors = [],
  products,
  onSubmit,
  defaultDates,
  availableTags,
  onProductChange,
  isFiltering = false,
  onValidityChange,
}, ref) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [replacementProduct, setReplacementProduct] = React.useState(null);
  const [dateFrom, setDateFrom] = React.useState(defaultDates?.from || null);
  const [dateTo, setDateTo] = React.useState(defaultDates?.to || null);
  const [tags, setTags] = React.useState([]);
  const [selectedMachineIds, setSelectedMachineIds] = React.useState([]);

  React.useEffect(() => {
    const nextFrom = defaultDates?.from || null;
    const nextTo = defaultDates?.to ?? (nextFrom ? computeDefaultEndDate(nextFrom) : null);
    setDateFrom(nextFrom);
    setDateTo(nextTo);
  }, [defaultDates?.from, defaultDates?.to]);

  const sectorMap = React.useMemo(() => {
    const map = new Map();
    (sectors || []).forEach(sector => {
      const sectorId = String(sector?.id ?? '');
      if (!sectorId) return;
      map.set(sectorId, {
        id: sectorId,
        name: sector?.name || sectorId,
        machines: (sector?.machines || []).map(machine => ({
          id: Number(machine?.id),
          alias: machine?.alias || `Machine ${machine?.id}`,
        })).filter(machine => Number.isFinite(machine.id))
      });
    });
    return map;
  }, [sectors]);

  const options = React.useMemo(() => {
    const entries = [];
    sectorMap.forEach(sector => {
      entries.push({
        type: 'sector',
        value: sector.id,
        label: sector.name,
        machineIds: sector.machines.map(machine => machine.id),
      });
      sector.machines.forEach(machine => {
        entries.push({
          type: 'machine',
          value: machine.id,
          label: machine.alias,
          sectorId: sector.id,
        });
      });
    });
    return entries;
  }, [sectorMap]);

  const machineOptionMap = React.useMemo(() => {
    const map = new Map();
    options.forEach(option => {
      if (option.type === 'machine') {
        map.set(option.value, option);
      }
    });
    return map;
  }, [options]);

  const selectedOptions = React.useMemo(() => (
    selectedMachineIds
      .map(id => machineOptionMap.get(id))
      .filter(Boolean)
  ), [selectedMachineIds, machineOptionMap]);

  const handleSelectionChange = React.useCallback((_, value, reason, details) => {
    if (reason === 'selectOption' && details?.option) {
      const option = details.option;
      if (option.type === 'sector') {
        setSelectedMachineIds(prev => {
          const set = new Set(prev);
          (option.machineIds || []).forEach(id => {
            if (machineOptionMap.has(id)) {
              set.add(id);
            }
          });
          return Array.from(set);
        });
      } else if (option.type === 'machine') {
        setSelectedMachineIds(value.map(item => item.value));
      }
    } else if (reason === 'removeOption') {
      setSelectedMachineIds(value.map(item => item.value));
    } else if (reason === 'clear') {
      setSelectedMachineIds([]);
    }
  }, [machineOptionMap]);

  const isValid = React.useMemo(() => (
    Boolean(
      selectedProduct &&
      replacementProduct &&
      selectedMachineIds.length > 0 &&
      dateFrom &&
      dateTo
    )
  ), [selectedProduct, replacementProduct, selectedMachineIds.length, dateFrom, dateTo]);

  React.useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleSubmit = React.useCallback(() => {
    if (!isValid) return false;
    return onSubmit({
      productId: selectedProduct.id,
      replacementProductId: replacementProduct.id,
      machineIds: selectedMachineIds,
      dateFrom: dateFrom ? formatLocalDate(dateFrom) : undefined,
      dateTo: dateTo ? formatLocalDate(dateTo) : undefined,
      tags,
    });
  }, [isValid, onSubmit, selectedProduct, replacementProduct, selectedMachineIds, dateFrom, dateTo, tags]);

  React.useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  React.useEffect(() => {
    if (!selectedMachineIds.length) return;
    const validIds = new Set(options.filter(opt => opt.type === 'machine').map(opt => opt.value));
    setSelectedMachineIds(prev => {
      const filtered = prev.filter(id => validIds.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [options, selectedMachineIds.length]);

  const durationDisplay = React.useMemo(() => {
    const rawWeeks = calculateDurationWeeks(dateFrom, dateTo);
    return formatWeeksDisplay(rawWeeks);
  }, [dateFrom, dateTo]);

  const handleDateFromChange = React.useCallback((value) => {
    setDateFrom(value);
    setDateTo(value ? computeDefaultEndDate(value) : null);
  }, []);

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        {t('bulkReplacementSubtitleShort')}
      </Typography>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <Autocomplete
          value={selectedProduct}
          onChange={(event, newValue) => {
            setSelectedProduct(newValue);
            setSelectedMachineIds([]);
            onProductChange?.(newValue?.id || null);
          }}
          options={products}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => <TextField {...params} label={t('productLabel')} size="small" />}
        />
        <Autocomplete
          value={replacementProduct}
          onChange={(event, newValue) => setReplacementProduct(newValue)}
          options={products}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => <TextField {...params} label={t('replacementProductLabel')} size="small" />}
        />
      </Box>

      <Autocomplete
        multiple
        value={selectedOptions}
        onChange={handleSelectionChange}
        options={options}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label || ''}
        isOptionEqualToValue={(option, value) => option.type === value.type && option.value === value.value}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} label={option.label} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('machinesLabel')}
            size="small"
            fullWidth
            disabled={isFiltering}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              paddingLeft: option.type === 'machine' ? 24 : 12,
              fontWeight: option.type === 'sector' ? 600 : undefined,
            }}
          >
            {option.label}
          </li>
        )}
      />

      <TagSelect
        label={t('tagsPlaceholder')}
        placeholder={t('tagsPlaceholder')}
        value={tags}
        onChange={setTags}
        availableTags={availableTags}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <DatePicker
          label={t('startLabel')}
          value={dateFrom}
          onChange={handleDateFromChange}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
        <DatePicker
          label={t('endLabel')}
          value={dateTo}
          onChange={setDateTo}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
      </Stack>

      {durationDisplay != null && (
        <Typography variant="caption" color="text.secondary">
          {`${t('experimentDurationLabel') || 'Duration'}: ${durationDisplay} ${
            durationDisplay === 1
              ? t('weeksLabelSingular') || 'week'
              : t('weeksLabelPlural') || 'weeks'
          }`}
        </Typography>
      )}

    </Stack>
  );
});

export default BulkReplacement;
